import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useAuth } from "../contexts/AuthContext";
import Topbar from "../components/Topbar";
import Avatar from "../components/Avatar";
import { Hash, Plus, Send, Video, Paperclip } from "lucide-react";

export default function Chat() {
  const { currentWorkspaceId, members, memberById } = useWorkspace();
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const scrollRef = useRef(null);

  const reloadChannels = async () => {
    if (!currentWorkspaceId) return;
    const r = await api.get("/channels", { params: { workspace_id: currentWorkspaceId } });
    setChannels(r.data);
    if (!activeId && r.data[0]) setActiveId(r.data[0].id);
  };

  useEffect(() => { reloadChannels(); }, [currentWorkspaceId]);

  useEffect(() => {
    if (!activeId) return;
    let alive = true;
    const fetchMsgs = async () => {
      try {
        const r = await api.get(`/channels/${activeId}/messages`);
        if (alive) setMessages(r.data);
      } catch (e) { /* ignore */ }
    };
    fetchMsgs();
    const t = setInterval(fetchMsgs, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [activeId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (extra = {}) => {
    if (!text.trim() && !extra.meet_link) return;
    await api.post(`/channels/${activeId}/messages`, {
      text: text || (extra.meet_link ? "📹 Video call" : ""), mentions: [], ...extra,
    });
    setText("");
    const r = await api.get(`/channels/${activeId}/messages`);
    setMessages(r.data);
  };

  const sendMeet = () => send({ meet_link: "https://meet.new" });

  const createChannel = async () => {
    if (!newName.trim()) return;
    await api.post("/channels", { name: newName, type: "channel", members: [] }, {
      params: { workspace_id: currentWorkspaceId },
    });
    setNewName("");
    setShowNew(false);
    reloadChannels();
  };

  const startDM = async (userId) => {
    const target = members.find((m) => m.id === userId);
    if (!target) return;
    const r = await api.post(
      "/channels",
      { name: `DM ${target.name}`, type: "dm", members: [userId] },
      { params: { workspace_id: currentWorkspaceId } },
    );
    await reloadChannels();
    setActiveId(r.data.id);
  };

  const active = channels.find((c) => c.id === activeId);

  return (
    <div data-testid="chat-page">
      <Topbar title="Chat" subtitle="Workspace channels and direct messages" breadcrumb="Communication" />
      <div className="flex h-[calc(100vh-77px)]">
        {/* Channel list */}
        <div className="w-64 border-r border-zinc-800 bg-[#0B0B0E] flex flex-col" data-testid="channel-list">
          <div className="p-3 border-b border-zinc-800 flex items-center justify-between">
            <span className="text-[10px] uppercase font-mono tracking-widest text-zinc-500">Channels</span>
            <button onClick={() => setShowNew(!showNew)} className="text-zinc-400 hover:text-white" data-testid="new-channel-btn">
              <Plus className="w-4 h-4" />
            </button>
          </div>
          {showNew && (
            <div className="p-3 border-b border-zinc-800">
              <input
                value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createChannel()}
                placeholder="channel-name"
                data-testid="new-channel-input"
                className="w-full bg-[#141417] border border-zinc-800 focus:border-[#D4A968] rounded px-2 py-1.5 text-sm text-white outline-none"
              />
              <button onClick={createChannel} className="mt-1.5 w-full text-xs bg-[#D4A968] hover:bg-[#E8C285] text-black py-1 rounded font-semibold">Create</button>
            </div>
          )}
          <div className="flex-1 overflow-y-auto py-2">
            {channels.filter((c) => c.type === "channel").map((c) => (
              <button
                key={c.id} onClick={() => setActiveId(c.id)}
                data-testid={`channel-${c.name}`}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm transition-colors ${
                  activeId === c.id ? "bg-[#1B1B1F] text-[#D4A968]" : "text-zinc-400 hover:bg-[#141417] hover:text-white"
                }`}
              >
                <Hash className="w-3.5 h-3.5" />
                {c.name}
              </button>
            ))}

            <div className="px-3 mt-4 mb-1.5 text-[10px] uppercase font-mono tracking-widest text-zinc-500">Direct messages</div>
            {channels.filter((c) => c.type === "dm").map((c) => {
              const otherIds = c.members.filter((m) => m !== user.id);
              const other = memberById(otherIds[0]);
              return (
                <button
                  key={c.id}
                  onClick={() => setActiveId(c.id)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm transition-colors ${
                    activeId === c.id ? "bg-[#1B1B1F] text-[#D4A968]" : "text-zinc-400 hover:bg-[#141417] hover:text-white"
                  }`}
                  data-testid={`dm-${c.id}`}
                >
                  <Avatar user={other} size="xs" />
                  <span className="truncate">{other?.name || c.name}</span>
                </button>
              );
            })}

            <div className="px-3 mt-4 mb-1.5 text-[10px] uppercase font-mono tracking-widest text-zinc-500">Start DM</div>
            {members.filter((m) => m.id !== user.id).map((m) => (
              <button key={m.id} onClick={() => startDM(m.id)} className="w-full flex items-center gap-2 px-3 py-1 text-xs text-zinc-500 hover:text-white">
                <Avatar user={m} size="xs" /> <span className="truncate">{m.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 flex flex-col">
          {active ? (
            <>
              <div className="px-5 py-3 border-b border-zinc-800 bg-[#0B0B0E] flex items-center gap-2">
                {active.type === "dm" ? (
                  <Avatar user={memberById(active.members.find((m) => m !== user.id))} size="sm" />
                ) : (
                  <Hash className="w-4 h-4 text-zinc-500" />
                )}
                <span className="text-sm font-bold text-white">{active.name}</span>
                <span className="text-[10px] text-zinc-500 font-mono ml-2">{active.members.length} members</span>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-3 space-y-3" data-testid="messages">
                {messages.map((m) => {
                  const u = memberById(m.user_id);
                  return (
                    <div key={m.id} className="flex items-start gap-2.5" data-testid={`message-${m.id}`}>
                      <Avatar user={u || { name: "?" }} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-zinc-400">
                          <span className="text-white font-semibold">{u?.name || "Unknown"}</span>
                          <span className="ml-2 font-mono text-zinc-600">{new Date(m.created_at).toLocaleTimeString()}</span>
                        </div>
                        <div className="text-sm text-zinc-200 mt-0.5">{m.text}</div>
                        {m.meet_link && (
                          <a href={m.meet_link} target="_blank" rel="noreferrer" data-testid={`meet-link-${m.id}`} className="mt-1 inline-flex items-center gap-1.5 bg-[#D4A968]/10 border border-[#D4A968]/40 text-[#D4A968] px-2.5 py-1 rounded text-xs font-semibold hover:bg-[#D4A968]/20">
                            <Video className="w-3 h-3" /> Join Google Meet
                          </a>
                        )}
                        {m.attachment_url && (
                          <a href={m.attachment_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-[#38BDF8]">
                            <Paperclip className="w-3 h-3" /> Attachment
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-zinc-800 px-5 py-3 flex items-center gap-2">
                <button onClick={sendMeet} title="Send Google Meet link" data-testid="send-meet-btn" className="bg-[#141417] border border-zinc-800 hover:border-[#D4A968] text-zinc-400 hover:text-[#D4A968] p-2 rounded-md">
                  <Video className="w-4 h-4" />
                </button>
                <input
                  value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={`Message ${active.type === "dm" ? "" : "#" + active.name}…`}
                  data-testid="chat-input"
                  className="flex-1 bg-[#141417] border border-zinc-800 focus:border-[#D4A968] rounded-md px-3 py-2 text-sm text-white outline-none"
                />
                <button onClick={() => send()} data-testid="chat-send-btn" className="bg-[#D4A968] hover:bg-[#E8C285] text-black font-semibold px-4 py-2 rounded-md text-sm flex items-center gap-1">
                  <Send className="w-3.5 h-3.5" /> Send
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">Select a channel</div>
          )}
        </div>
      </div>
    </div>
  );
}
